import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Botao, Card, Checkbox, Chip, ErrorState, Field, Input, Nota, Skeleton } from '../../../ui';
import {
  ConfiguracaoEntrega,
  buscarConfiguracaoEntrega,
  salvarConfiguracaoEntrega,
} from '../../../api/contrato/entregaContratoApi';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Liga/desliga o envio automático do contrato por WhatsApp (at-40, item 43;
 * reescrito na at-69, frente D/D7).
 *
 * ⚠️ A tela **não replica a regra** de quando é possível ligar: quem recusa é o
 * backend (sem credencial no ambiente ou sem template aprovado). O `podeLigar`
 * daqui é **pré-checagem**, para o TI não descobrir no erro — integração ligada e
 * inutilizável faria todo envio falhar em silêncio, associado por associado.
 *
 * ## O que mudou na at-69
 *
 *   - 🔴 **O card deixou de SUMIR quando a configuração falha.** Era
 *     `toast.error` (que passa) e `if (!config) return null` — o bloco inteiro
 *     desaparecia da tela, e o TI não tinha como distinguir "não existe" de "não
 *     carregou". Agora é `ErrorState` com "Tentar novamente".
 *   - 🔴 **O template passou a ser salvo por BOTÃO** (A5). Era gravado no `onBlur`:
 *     sair do campo disparava o `PUT` sem aviso, e fechar a aba antes do blur
 *     perdia a edição. O liga/desliga continua imediato — é um toggle.
 *   - **O rótulo do toggle é ESTÁVEL** ("Envio automático pelo WhatsApp") e o
 *     estado vai num `Chip` ao lado. Antes o único texto do controle mudava com o
 *     estado ("LIGADO"/"desligado"), o que obriga a ler para saber onde se está.
 *   - **O motivo de não poder ligar é texto visível**, e cobre os dois casos — o
 *     aviso antigo só falava de credencial, nunca do template em branco.
 */

type Estado = 'carregando' | 'ok' | 'erro';

const EnvioWhatsAppCard: React.FC = () => {
  const [config, setConfig] = useState<ConfiguracaoEntrega | null>(null);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const [templateId, setTemplateId] = useState('');
  const [salvandoTemplate, setSalvandoTemplate] = useState(false);
  const [alternando, setAlternando] = useState(false);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      const dados = await buscarConfiguracaoEntrega();
      setConfig(dados);
      setTemplateId(dados.templateId);
      setEstado('ok');
    } catch (falha) {
      setConfig(null);
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar a configuração de envio.'));
      setEstado('erro');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvar = async (payload: { ativo?: boolean; templateId?: string }) => {
    const ehToggle = payload.ativo !== undefined;
    const marcarOcupado = ehToggle ? setAlternando : setSalvandoTemplate;

    marcarOcupado(true);
    try {
      const dados = await salvarConfiguracaoEntrega(payload);
      setConfig(dados);
      setTemplateId(dados.templateId);
      toast.success('Configuração de envio atualizada.');
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível salvar a configuração.'));
      // Recarrega para a tela não ficar mostrando um estado que o servidor recusou.
      void carregar();
    } finally {
      marcarOcupado(false);
    }
  };

  const corpo = () => {
    if (estado === 'carregando') return <Skeleton linhas={2} altura="tw-h-12" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar a configuração de envio."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (!config) return null;

    const podeLigar = config.credenciaisConfiguradas && Boolean(templateId.trim());
    const templateSalvo = templateId.trim() === config.templateId;

    /* Os dois motivos possíveis, ditos por extenso. O aviso antigo só cobria a
       credencial — quem tinha credencial e nenhum template via um controle
       travado sem explicação. */
    const motivoTravado = !config.ativo && !podeLigar
      ? !config.credenciaisConfiguradas && !templateId.trim()
        ? 'Para ligar: informe o modelo de mensagem aprovado e peça à infraestrutura as credenciais do OctaDesk neste ambiente.'
        : !config.credenciaisConfiguradas
          ? 'As credenciais do OctaDesk não estão neste ambiente. Enquanto isso, o envio automático não pode ser ligado e o consultor envia manualmente.'
          : 'Informe o modelo de mensagem aprovado abaixo e salve — sem ele o envio automático não pode ser ligado.'
      : undefined;

    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Nota>
          Com o envio ligado, o contrato é enviado ao associado automaticamente pela plataforma
          OctaDesk, usando o modelo de mensagem aprovado. Desligado, o consultor continua enviando
          pelo WhatsApp manualmente — <strong>o link é o mesmo</strong>.
        </Nota>

        <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-3">
          <Checkbox
            /* Rótulo ESTÁVEL: o estado vai no chip ao lado, não no texto do
               controle (que antes mudava de "LIGADO" para "desligado"). */
            rotulo="Envio automático pelo WhatsApp"
            checked={config.ativo}
            disabled={alternando || (!config.ativo && !podeLigar)}
            motivoDesabilitado={motivoTravado}
            onChange={(marcado) => void salvar({ ativo: marcado })}
          />
          <Chip variante={config.ativo ? 'ok' : 'neutro'}>
            {config.ativo ? 'Ligado' : 'Desligado'}
          </Chip>
        </div>

        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <Field
            rotulo="Modelo de mensagem aprovado (id ou código)"
            dica="Criado no painel do OctaDesk e aprovado pela Meta. As variáveis do modelo são preenchidas pelo sistema: nome do associado, tipo do contrato e link."
            className="tw-min-w-0 tw-flex-1 tw-basis-72"
            campo="templateId"
          >
            <Input
              value={templateId}
              disabled={salvandoTemplate}
              onChange={(evento) => setTemplateId(evento.target.value)}
            />
          </Field>

          {/* 🔴 Botão, não `onBlur` (A5): gravação implícita era o único caso do
              app, e fechar a aba antes do blur perdia a edição. */}
          <Botao
            variante="secundario"
            disabled={templateSalvo}
            carregando={salvandoTemplate}
            onClick={() => void salvar({ templateId: templateId.trim() })}
          >
            Salvar
          </Botao>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
        <h2 className="tw-m-0 tw-font-marca tw-text-base tw-font-bold tw-text-texto-titulo">
          Envio do contrato por WhatsApp
        </h2>
        {corpo()}
      </div>
    </Card>
  );
};

export default EnvioWhatsAppCard;
